import type { TypeVoxels } from './edit/log.ts';
// Dataset format (see prep/cvformat.py) and a JS decoder for one record.
import { octDecode, type V3 } from './math.ts';
import { RECORD, RANGE_STEP, type ChunkIndex } from './lod.ts';

export interface Frame { label?: string; floorZ: number; north?: [number, number]; latlon?: [number, number] }
export interface CloudMeta {
  id: string; name: string; frame: string; notes: string; fit: number | null; color: string; frameIds?: boolean;
  points: number; chunks: number; bytes: number; bbox: number[]; index: string;
  // from single-scan edits (prep/edits.py): a split part or a movable object of scan `parent`
  kind?: 'scan' | 'part' | 'object'; parent?: string; T?: { R: number[]; t: number[] };
  of?: string;   // an object's type ("bench"), when set in the object editor
  F?: { R: number[]; t: number[] };   // ...and its fit onto the type's reference instance (log.ts Obj), for drawing it as the type's voxels
  edit?: { head: string; deleted?: number; by?: string };
}
/** orig/manifest.json: the unedited build of each scan, for the editor. */
export interface OrigEntry {
  index: string; bin: string; pid: string; frame: string | null; n: number;
  cameras?: string | null;   // <id>.<hash>.cameras.json: OrigCameras
  links?: string | null;     // <id>.<hash>.links.json: OrigLinks
  images?: string | null;    // <id>.<hash>.images.json: OrigImages
  features?: string | null;  // <id>.<hash>.features.json: OrigFeatures
}
/**
 * RTAB-Map's keypoints of a scan (prep/features.py): its colour image size (w x h, before images.py scaled it),
 * and per keypoint [frame, word, x, y (pixel), X, Y, Z (its point in the frame's base frame; 0, 0, 0: none)].
 */
export interface OrigFeatures { w: number; h: number; frames: number[]; kp: number[][] }
/**
 * A scan's camera images (prep/images.py), poses in its file frame: per frame the optical camera (x right,
 * y down, z forward; 12: R row-major, t) and its depth grid's intrinsics [fx, fy, cx, cy]. dir (beside the
 * index in orig/) holds <frame>.jpg (w x h) and <frame>.d16 (dw x dh little-endian uint16 mm, 0 = none).
 */
export interface OrigImages { frames: number[]; w: number; h: number; dw: number; dh: number; K: number[][]; pose: number[][]; dir: string }
/** A scan's camera path in its file frame (not moved by its alignment tree): frame ids and their positions [x, y, z, ...]. */
export interface OrigCameras { frames: number[]; xyz: number[] }
/**
 * A scan's pose graph (prep/links.py) in its file frame: each frame's loop-closed pose (12: R row-major, t),
 * and RTAB-Map's links [a, b, type (0 neighbour, 1 loop closure, 2 local closure), wt, wr (information:
 * 1 / variance of translation, rotation), ...12: b's pose in a's frame as measured].
 */
export interface OrigLinks { frames: number[]; pose: number[][]; links: number[][] }
export interface Scene {
  version: number; name: string; frames: Record<string, Frame>; spacing: number[];
  clouds: CloudMeta[]; overlays?: { file: string; sectionHeights: number[] } | null;
  cameras?: string | null;   // cameras.<hash>.json: CameraPaths
  /** Per object type, its voxel model (log.ts TypeVoxels) and F: its reference instance's fit; each object cloud of the type with an F shows it at F⁻¹ · F (src/edit/voxels.ts). */
  voxels?: Record<string, TypeVoxels & { F: { R: number[]; t: number[] } }>;
}
/** Each scan's camera path (one position per frame, in frame order) in the scene frame: {cloud id: [x, y, z, ...]}. */
export type CameraPaths = Record<string, number[]>;
export interface CloudIndex {
  version: number; record: number; spacing: number[]; points: number; bbox: number[];
  levelOffsets: number[]; chunks: ChunkIndex[]; bin: string;
  /** The records' edge bytes have the side (bit 2; prep/edges.py's second format). */
  edgeSign?: boolean;
}
export interface Overlays {
  sections: { scan: string; h: number; frame: string; lines: number[][] }[];
  outlines: { label: string; frame: string; loops: number[][] }[];
}

// frame: source frame % 64; range: capture range (m); edge: in its depth image, Sobel strength 0-1, on a Canny edge,
// on the far (background) side of the depth around it (meaningful only with the index's edgeSign)
export interface Point { p: V3; rgb: V3; n: V3 | null; frame: number | null; range: number | null; edge: { sobel: number; canny: boolean; far: boolean } | null }

/** Record i of a buffer of records `rec` bytes long (the index's `record`: 12 in a version 1 build). */
export function decodeRecord(buf: ArrayBuffer | DataView, i: number, ch: ChunkIndex, rec = RECORD): Point {
  const d = buf instanceof DataView ? buf : new DataView(buf);
  const o = i * rec, r = rec > 12 ? d.getUint8(o + 12) : 0, e = rec > 13 ? d.getUint8(o + 13) : 0;
  const p: V3 = [0, 0, 0];
  for (let k = 0; k < 3; k++) p[k] = ch.origin[k] + (d.getUint16(o + 2 * k, true) / 65535) * ch.size[k];
  const flags = d.getUint8(o + 9);
  return {
    p,
    rgb: [d.getUint8(o + 6), d.getUint8(o + 7), d.getUint8(o + 8)],
    n: flags & 1 ? octDecode(d.getUint8(o + 10), d.getUint8(o + 11)) : null,
    frame: flags & 2 ? flags >> 2 : null,
    range: r ? r * RANGE_STEP : null,
    edge: e & 1 ? { sobel: (e >> 3) / 31, canny: (e & 2) !== 0, far: (e & 4) !== 0 } : null,
  };
}
